/**
 * Base Map Form Component
 * 
 * Form for creating and editing base maps with all configuration fields.
 * Requirements: 29.1, 29.2, 29.3
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  SpaceBetween,
  Container,
  Header,
  FormField,
  Input,
  Select,
  Textarea,
  Button,
  ColumnLayout,
  Alert,
  StatusIndicator
} from '@cloudscape-design/components';
import { baseMapsApi } from '../../services/baseMapsApi';
import type { BaseMapFormData, ProjectionParameters } from '../../types/baseMaps';
import { validateBaseMapForm, getFieldError, type ValidationError } from '../../utils/baseMapValidation';

interface BaseMapFormProps {
  mode: 'create' | 'edit';
}

const BaseMapForm: React.FC<BaseMapFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // Form state
  const [formData, setFormData] = useState<BaseMapFormData>({
    name: '',
    type: 'single-tile',
    description: '',
    thumbnailPath: '',
    overviewUrl: '',
    geoTiffUrl: '',
    tileIndexPath: '',
    bounds: {
      north: 0,
      south: 0,
      east: 0,
      west: 0
    },
    projection: {
      type: 'lcc',
      centralMeridian: 0,
      originLatitude: 0,
      standardParallel1: 0,
      standardParallel2: 0
    },
    active: true,
    updateCycle: '',
    dpi: 300,
    datum: 'WGS84'
  });

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadBaseMap(id);
    }
  }, [mode, id]);

  const loadBaseMap = async (baseMapId: string) => {
    setLoading(true);
    try {
      const response = await baseMapsApi.getBaseMap(baseMapId);
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        const baseMap = response.data.baseMap;
        setFormData({
          id: baseMap.id,
          name: baseMap.name,
          type: baseMap.type,
          description: baseMap.description || '',
          thumbnailPath: baseMap.thumbnailPath,
          overviewUrl: baseMap.overviewUrl || '',
          geoTiffUrl: baseMap.geoTiffUrl || '',
          tileIndexPath: baseMap.tileIndexPath || '',
          bounds: baseMap.bounds || { north: 0, south: 0, east: 0, west: 0 },
          projection: baseMap.projection,
          active: baseMap.active,
          updateCycle: baseMap.updateCycle || '',
          dpi: baseMap.dpi || 300,
          datum: baseMap.datum || 'WGS84'
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load base map');
    } finally {
      setLoading(false);
    }
  };

  const handleExtractMetadata = async () => {
    if (!formData.geoTiffUrl) {
      setError('Please enter a GeoTIFF URL first');
      return;
    }

    setExtracting(true);
    setError(null);

    try {
      const response = await baseMapsApi.extractMetadata(
        formData.id || 'temp',
        formData.geoTiffUrl
      );

      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        const metadata = response.data.metadata;
        setFormData({
          ...formData,
          projection: metadata.projection,
          bounds: metadata.bounds,
          dpi: metadata.dpi || formData.dpi
        });
        setSuccess('Metadata extracted successfully');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extract metadata');
    } finally {
      setExtracting(false);
    }
  };

  const handleTestPreview = () => {
    // Open Print Map Maker with current form values as URL parameters
    const params = new URLSearchParams({
      baseMapId: formData.id || 'preview',
      baseMapName: formData.name,
      baseMapType: formData.type,
      overviewUrl: formData.overviewUrl || '',
      preview: 'true'
    });
    window.open(`/print-map-maker?${params.toString()}`, '_blank');
  };

  const handleSave = async () => {
    // Validate form data
    const validation = validateBaseMapForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      setError('Please fix the validation errors before saving');
      return;
    }

    setValidationErrors([]);
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let response;
      if (mode === 'create') {
        response = await baseMapsApi.createBaseMap(formData);
      } else if (id) {
        response = await baseMapsApi.updateBaseMap(id, formData);
      }

      if (response?.error) {
        setError(response.error);
      } else {
        setSuccess(`Base map ${mode === 'create' ? 'created' : 'updated'} successfully`);
        setTimeout(() => navigate('/admin/base-maps'), 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save base map');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/base-maps');
  };

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleTestPreview} disabled={!formData.overviewUrl}>
            Test Preview
          </Button>
          <Button variant="primary" onClick={handleSave} loading={loading}>
            Save
          </Button>
        </SpaceBetween>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success" dismissible onDismiss={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Basic Info Section */}
        <Container header={<Header variant="h2">Basic Information</Header>}>
          <SpaceBetween size="m">
            <FormField 
              label="Name" 
              description="Display name for the base map"
              errorText={getFieldError(validationErrors, 'name')}
            >
              <Input
                value={formData.name}
                onChange={({ detail }) => setFormData({ ...formData, name: detail.value })}
                placeholder="e.g., US VFR Wall Planning Chart"
              />
            </FormField>

            <FormField label="Type" description="Single-tile or multi-tile base map">
              <Select
                selectedOption={{ label: formData.type === 'single-tile' ? 'Single Tile' : 'Multi-Tile', value: formData.type }}
                onChange={({ detail }) =>
                  setFormData({ ...formData, type: detail.selectedOption.value as 'single-tile' | 'multi-tile' })
                }
                options={[
                  { label: 'Single Tile', value: 'single-tile' },
                  { label: 'Multi-Tile', value: 'multi-tile' }
                ]}
              />
            </FormField>

            <FormField label="Description" description="Optional description of the base map">
              <Textarea
                value={formData.description || ''}
                onChange={({ detail }) => setFormData({ ...formData, description: detail.value })}
                placeholder="e.g., FAA VFR Wall Planning Chart covering the continental United States"
                rows={3}
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {/* Files Section */}
        <Container header={<Header variant="h2">Files and URLs</Header>}>
          <SpaceBetween size="m">
            <FormField 
              label="Thumbnail Path" 
              description="S3 path to thumbnail image"
              errorText={getFieldError(validationErrors, 'thumbnailPath')}
            >
              <Input
                value={formData.thumbnailPath}
                onChange={({ detail }) => setFormData({ ...formData, thumbnailPath: detail.value })}
                placeholder="e.g., us-vfr-wall-planning/thumbnail.jpg"
              />
            </FormField>

            {formData.type === 'single-tile' && (
              <>
                <FormField label="Overview URL" description="CloudFront URL to web-optimized overview image">
                  <Input
                    value={formData.overviewUrl || ''}
                    onChange={({ detail }) => setFormData({ ...formData, overviewUrl: detail.value })}
                    placeholder="e.g., https://d279ayt2oe6k6r.cloudfront.net/us-vfr-wall-planning/overview.tif"
                  />
                </FormField>

                <FormField label="GeoTIFF URL" description="CloudFront URL to full-resolution GeoTIFF">
                  <Input
                    value={formData.geoTiffUrl || ''}
                    onChange={({ detail }) => setFormData({ ...formData, geoTiffUrl: detail.value })}
                    placeholder="e.g., https://d279ayt2oe6k6r.cloudfront.net/us-vfr-wall-planning/map.tif"
                  />
                </FormField>
              </>
            )}

            {formData.type === 'multi-tile' && (
              <FormField label="Tile Index Path" description="S3 path to tile index JSON">
                <Input
                  value={formData.tileIndexPath || ''}
                  onChange={({ detail }) => setFormData({ ...formData, tileIndexPath: detail.value })}
                  placeholder="e.g., vfr-sectionals/tile-index.json"
                />
              </FormField>
            )}

            <Button onClick={handleExtractMetadata} loading={extracting} disabled={!formData.geoTiffUrl}>
              Extract Metadata from GeoTIFF
            </Button>
          </SpaceBetween>
        </Container>

        {/* Geographic Bounds Section */}
        <Container header={<Header variant="h2">Geographic Bounds</Header>}>
          <ColumnLayout columns={2}>
            <FormField label="North Latitude" description="Northern boundary in degrees">
              <Input
                type="number"
                value={formData.bounds.north.toString()}
                onChange={({ detail }) =>
                  setFormData({
                    ...formData,
                    bounds: { ...formData.bounds, north: parseFloat(detail.value) || 0 }
                  })
                }
              />
            </FormField>

            <FormField label="South Latitude" description="Southern boundary in degrees">
              <Input
                type="number"
                value={formData.bounds.south.toString()}
                onChange={({ detail }) =>
                  setFormData({
                    ...formData,
                    bounds: { ...formData.bounds, south: parseFloat(detail.value) || 0 }
                  })
                }
              />
            </FormField>

            <FormField label="East Longitude" description="Eastern boundary in degrees">
              <Input
                type="number"
                value={formData.bounds.east.toString()}
                onChange={({ detail }) =>
                  setFormData({
                    ...formData,
                    bounds: { ...formData.bounds, east: parseFloat(detail.value) || 0 }
                  })
                }
              />
            </FormField>

            <FormField label="West Longitude" description="Western boundary in degrees">
              <Input
                type="number"
                value={formData.bounds.west.toString()}
                onChange={({ detail }) =>
                  setFormData({
                    ...formData,
                    bounds: { ...formData.bounds, west: parseFloat(detail.value) || 0 }
                  })
                }
              />
            </FormField>
          </ColumnLayout>
        </Container>

        {/* Projection Parameters Section */}
        <Container header={<Header variant="h2">Projection Parameters</Header>}>
          <SpaceBetween size="m">
            <FormField label="Projection Type" description="Map projection system">
              <Select
                selectedOption={{
                  label: {
                    lcc: 'Lambert Conformal Conic',
                    mercator: 'Mercator',
                    albers: 'Albers Equal Area',
                    utm: 'UTM'
                  }[formData.projection.type],
                  value: formData.projection.type
                }}
                onChange={({ detail }) =>
                  setFormData({
                    ...formData,
                    projection: { ...formData.projection, type: detail.selectedOption.value as any }
                  })
                }
                options={[
                  { label: 'Lambert Conformal Conic', value: 'lcc' },
                  { label: 'Mercator', value: 'mercator' },
                  { label: 'Albers Equal Area', value: 'albers' },
                  { label: 'UTM', value: 'utm' }
                ]}
              />
            </FormField>

            {formData.projection.type === 'lcc' && (
              <ColumnLayout columns={2}>
                <FormField label="Central Meridian" description="Longitude of central meridian (-180 to 180)">
                  <Input
                    type="number"
                    value={formData.projection.centralMeridian?.toString() || ''}
                    onChange={({ detail }) =>
                      setFormData({
                        ...formData,
                        projection: { ...formData.projection, centralMeridian: parseFloat(detail.value) || 0 }
                      })
                    }
                  />
                </FormField>

                <FormField label="Origin Latitude" description="Latitude of projection origin (-90 to 90)">
                  <Input
                    type="number"
                    value={formData.projection.originLatitude?.toString() || ''}
                    onChange={({ detail }) =>
                      setFormData({
                        ...formData,
                        projection: { ...formData.projection, originLatitude: parseFloat(detail.value) || 0 }
                      })
                    }
                  />
                </FormField>

                <FormField label="Standard Parallel 1" description="First standard parallel (-90 to 90)">
                  <Input
                    type="number"
                    value={formData.projection.standardParallel1?.toString() || ''}
                    onChange={({ detail }) =>
                      setFormData({
                        ...formData,
                        projection: { ...formData.projection, standardParallel1: parseFloat(detail.value) || 0 }
                      })
                    }
                  />
                </FormField>

                <FormField label="Standard Parallel 2" description="Second standard parallel (-90 to 90)">
                  <Input
                    type="number"
                    value={formData.projection.standardParallel2?.toString() || ''}
                    onChange={({ detail }) =>
                      setFormData({
                        ...formData,
                        projection: { ...formData.projection, standardParallel2: parseFloat(detail.value) || 0 }
                      })
                    }
                  />
                </FormField>
              </ColumnLayout>
            )}
          </SpaceBetween>
        </Container>

        {/* Metadata Section */}
        <Container header={<Header variant="h2">Metadata</Header>}>
          <ColumnLayout columns={3}>
            <FormField label="Update Cycle" description="How often the chart is updated">
              <Input
                value={formData.updateCycle || ''}
                onChange={({ detail }) => setFormData({ ...formData, updateCycle: detail.value })}
                placeholder="e.g., 56 days"
              />
            </FormField>

            <FormField label="DPI" description="Resolution in dots per inch">
              <Input
                type="number"
                value={formData.dpi?.toString() || ''}
                onChange={({ detail }) => setFormData({ ...formData, dpi: parseInt(detail.value) || 300 })}
              />
            </FormField>

            <FormField label="Datum" description="Geodetic datum">
              <Input
                value={formData.datum || ''}
                onChange={({ detail }) => setFormData({ ...formData, datum: detail.value })}
                placeholder="e.g., WGS84"
              />
            </FormField>
          </ColumnLayout>
        </Container>
      </SpaceBetween>
    </Form>
  );
};

export default BaseMapForm;
